import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'env/environment';
import { getAuth } from 'firebase/auth';
import { from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

export interface TrustedContact {
  id: string;
  token: string;
  name: string;
  phone: string;
  type: string;
  used: boolean;
  redeemedBy: string | null;
  createdAt: string | null;
  redeemedAt: string | null;
}

export interface TrustedContactsResponse {
  contacts: TrustedContact[];
  count: number;
  max: number;
}

export interface InviteRedeemResponse {
  success: boolean;
  isPromo: boolean;
  trustIdentities?: boolean;
}

@Injectable({ providedIn: 'root' })
export class InviteService {
  constructor(private http: HttpClient) {}

  createInvite(phone: string, legalName: string): Observable<{ token: string; contact: TrustedContact }> {
    return this.withAuthHeaders().pipe(
      switchMap(headers => {
        const url = `https://${environment.apiBaseURL}.cloudfunctions.net/createInvite`;
        return this.http.post<{ token: string; contact: TrustedContact }>(
          url,
          { phone, legalName },
          { headers }
        );
      })
    );
  }

  listTrustedContacts(): Observable<TrustedContactsResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers => {
        const url = `https://${environment.apiBaseURL}.cloudfunctions.net/listTrustedContacts`;
        return this.http.get<TrustedContactsResponse>(url, { headers });
      })
    );
  }

  deleteTrustedContact(contactId: string): Observable<{ success: true; contactId: string }> {
    return this.withAuthHeaders().pipe(
      switchMap(headers => {
        const url = `https://${environment.apiBaseURL}.cloudfunctions.net/deleteTrustedContact`;
        return this.http.post<{ success: true; contactId: string }>(
          url,
          { contactId },
          { headers }
        );
      })
    );
  }

  redeemInvite(token: string, phone: string): Observable<InviteRedeemResponse> {
    const auth = getAuth();
    const user = auth.currentUser;
    const url = `https://${environment.apiBaseURL}.cloudfunctions.net/redeemInvite`;

    if (!user) {
      return this.http.post<InviteRedeemResponse>(url, { token, phone });
    }

    return from(user.getIdToken()).pipe(
      switchMap(idToken => {
        const headers = new HttpHeaders({ Authorization: `Bearer ${idToken}` });
        return this.http.post<InviteRedeemResponse>(url, { token, phone }, { headers });
      })
    );
  }

  private withAuthHeaders(): Observable<HttpHeaders> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Not authenticated');
    }

    return from(user.getIdToken()).pipe(
      map(idToken => new HttpHeaders({ Authorization: `Bearer ${idToken}` }))
    );
  }
}
